import type { LayerTreeGroupNode } from '@/types/layerTree'

/**
 * Where a leaf node's active/inactive state lives, and whether that bucket is
 * single-select.
 *
 * `activeNodesByGroup` is keyed by `scopeId` — *not* by the leaf's immediate
 * parent group. Keying it by the immediate parent made exclusivity stop at
 * sub-group boundaries, so a leaf in a sub-group and a leaf in a sibling group
 * could both stay active even though the branch above them is exclusive.
 */
export interface SelectionScope {
  /** Group node id used as the `activeNodesByGroup` key for leaves in this scope. */
  scopeId: number
  /** Whether leaves in this scope are single-select (radio) or multi (checkbox). */
  exclusive: boolean
}

/**
 * Scopes are handed to components as props and recomputed on every render, so
 * they are interned: equal scopes are the same object and a re-render does not
 * look like a prop change. Keyed by group pk + flag, so the cache stays tiny and
 * never goes stale.
 */
const interned = new Map<string, SelectionScope>()

function scopeOf(scopeId: number, exclusive: boolean): SelectionScope {
  const key = `${scopeId}:${exclusive}`
  let scope = interned.get(key)
  if (!scope) {
    scope = { scopeId, exclusive }
    interned.set(key, scope)
  }
  return scope
}

/** Scope for the children of the tree's root group. */
export function rootScope(root: LayerTreeGroupNode): SelectionScope {
  return scopeOf(root.id, root.exclusive_selection)
}

/**
 * Scope for the children of `group`, given the scope `group` itself lives in.
 *
 * Every group's own `exclusive_selection` flag still decides how its children
 * behave; the flag only additionally controls whether the group *joins* the
 * scope above it:
 *
 * - exclusive group inside an exclusive scope → extends that scope, so a single
 *   selection is shared across all its sub-groups (the "one raster at a time in
 *   the whole top-level group" rule).
 * - exclusive group inside a non-exclusive scope → starts a fresh single-select
 *   scope of its own, independent of its siblings.
 * - non-exclusive group → always starts a fresh multi-select scope of its own,
 *   so ticking several of its layers never disturbs an exclusive branch above.
 */
export function childScope(parent: SelectionScope, group: LayerTreeGroupNode): SelectionScope {
  if (!group.exclusive_selection) return scopeOf(group.id, false)
  if (parent.exclusive) return parent
  return scopeOf(group.id, true)
}

/**
 * Scope for the children of `group` when `group` may be the tree root.
 * Convenience for recursive walkers that start with no scope in hand.
 */
export function descendScope(parent: SelectionScope | undefined, group: LayerTreeGroupNode): SelectionScope {
  return parent ? childScope(parent, group) : rootScope(group)
}
